import { Paper, Stack } from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { ReactElement, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { ChipFilterBar } from '@/components/FilterChips/ChipFilterBar';
import { useLiveGraphQL } from '@/hooks/useLiveGraphQL';
import { useCalendarDay, useNow } from '@/hooks/useNow';
import { alert } from '@/services/alert';
import { getRateLimitAlert, isMedplumRateLimitError } from '@/utils/medplumRateLimit';
import { ALL_POST_CONSULTATION_CONSULTS_QUERY } from '../graphql';
import { usePatientListFilters } from '../hooks/usePatientListFilters';
import { usePacQueueHydration } from '../hooks/usePacQueueHydration';
import { buildPostConsultationRows, PostConsultationLeadRow } from '../services/postConsultationRows';
import {
  buildConsultFilter,
  isInConsultScope,
  postConsultationChips,
  postConsultationSubscriptionParams,
} from '../services/postConsultationFilters';
import { ConsultAppointmentResult } from '../services/listRowShared';
import { COHORT_ROW_CAP, QUEUE_PAGE_SIZE, shouldWarnTruncated } from '../services/cohortLoading';
import { lastCommunicationFloor } from '../services/pacQueueHydration';
import { useCohortTotal } from '../hooks/useCohortTotal';
import { CohortTruncationNotice } from './CohortTruncationNotice';
import { ListRangeControl } from '@/components/Hub/ListRangeControl';
import { getCiQueueRowProps } from './listRowStyle';
import { usePostConsultationColumns } from './postConsultationColumns';

/**
 * The post-consultation CI queue.
 *
 * One row per patient, on their latest consult — NP or PN (BAR-2591). Sorted by
 * consult date, newest first: the freshest consults sit where the decision
 * window is still open.
 *
 * Ten columns. Still the widest surface in the system and it scrolls below
 * ~1800px, which is the point where a per-role column preset stops being
 * optional. Recorded, not solved here.
 */

/**
 * Rows per page. `useLiveGraphQL` injects this as `_count` on the root list, so
 * the query must NOT set one itself or Medplum rejects the whole request.
 *
 * Pagination is ON deliberately. With it off the hook loops on `_offset` until a
 * short page — fetching every matching row up front, and since each root row
 * fans out into several nested reverse-chains, that is what exhausted Medplum's
 * 50k-point budget and returned "Too Many Requests" with a null list, which the
 * tab renders as an empty queue with nothing naming the cause.
 *
 * Paginating bounds the work to what the rep has actually scrolled to.
 */
/**
 * Module-scope so the hydration effect keys on content, not on a fresh object
 * every render. No intro call any more: the column went with BAR-2591, and
 * Location now reads off whichever intake card exists via `callNotes`.
 */
const HYDRATION_NEEDS = {
  procedure: true,
  consultNotes: true,
  callNotes: true,
  careTeam: true,
  tasks: true,
  // The Location column's last resort, for a lead whose market was never
  // charted on a call (BAR-2597).
  eligibility: true,
} as const;

export interface PostConsultationTabProps {
  /** Injectable for tests; defaults to a clock that keeps ticking. */
  now?: Date;
}

export function PostConsultationTab({ now }: PostConsultationTabProps): ReactElement {
  const navigate = useNavigate();
  // Ticks, so a consult booked for 2pm turns into a row at 2pm and the queue
  // rolls over at midnight without a reload (BAR-2708).
  const resolvedNow = useNow(now);
  // Day-granular consumers key on the day, so the column model and the chip
  // definitions are rebuilt once at midnight rather than every tick.
  const today = useCalendarDay(resolvedNow);
  const chipDefinitions = useMemo(() => postConsultationChips(today), [today]);

  const filters = usePatientListFilters<PostConsultationLeadRow>({
    resetKey: 'all-post-consultation',
    chipDefinitions,
    now: resolvedNow,
  });

  const variables = useMemo(() => ({ filter: buildConsultFilter(filters.range) }), [filters.range]);
  const subscriptionParams = useMemo(() => postConsultationSubscriptionParams(), []);

  const { data, loading, error, scrollPagination, cohortTruncated } = useLiveGraphQL(
    ALL_POST_CONSULTATION_CONSULTS_QUERY,
    variables,
    {
      enablePagination: true,
      pageSize: QUEUE_PAGE_SIZE,
      autoLoadUpTo: COHORT_ROW_CAP,
      subscriptionParams,
    }
  );

  // Only the notice needs the real total, and only when the cap bit.
  const cohortTotal = useCohortTotal('Appointment', cohortTruncated ? variables.filter : undefined);

  useEffect(() => {
    if (error && isMedplumRateLimitError(error)) {
      alert.warning({
        ...getRateLimitAlert(
          `The system temporarily limited how fast we can load the post-consultation queue.
          Wait a few seconds before trying again.`
        ),
      });
    }
  }, [error]);

  // The subscription is coarser than the query (type only), so what it merges
  // is scoped here: a consult cancelled or rescheduled out of range leaves the
  // way the query would have left it out.
  const consults = useMemo(
    () =>
      error ? [] : (data as ConsultAppointmentResult[]).filter((consult) => isInConsultScope(consult, filters.range)),
    [data, error, filters.range]
  );

  const patientRefs = useMemo(
    () => [
      ...new Set(
        consults
          .flatMap((consult) => consult.participant ?? [])
          .map((participant) => participant.actor?.resource)
          .filter((resource) => resource?.resourceType === 'Patient')
          .map((resource) => `Patient/${(resource as { id?: string }).id}`)
      ),
    ],
    [consults]
  );

  const hydration = usePacQueueHydration(
    patientRefs,
    { ...HYDRATION_NEEDS, lastCommunicationSince: lastCommunicationFloor(today) },
    `all-post-consultation:${variables.filter}`
  );

  const rows = useMemo(
    () => buildPostConsultationRows(consults, hydration, resolvedNow),
    [consults, hydration, resolvedNow]
  );

  const visibleRows = useMemo(() => filters.chips.apply(rows), [filters.chips, rows]);
  const columns = usePostConsultationColumns(today);

  const table = useMantineReactTable({
    columns,
    data: visibleRows,
    initialState: { density: 'xs' as const, sorting: [{ id: 'consultDate', desc: true }] },
    state: { isLoading: loading || hydration.loading },
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableRowActions: false,
    enableRowSelection: false,
    enableRowVirtualization: true,
    enableToolbarInternalActions: false,
    mantineTableProps: { highlightOnHover: true },
    mantineTableBodyRowProps: ({ row }) =>
      getCiQueueRowProps(row.original, (patientId) => navigate(`/Patient/${patientId}/timeline`)),
    mantinePaperProps: {
      shadow: 'sm',
      withBorder: true,
      style: { height: '100%', display: 'flex', flexDirection: 'column' },
    },
    mantineTableContainerProps: scrollPagination?.tableContainerProps,
    mantineTableHeadCellProps: { style: { fontWeight: '500' } },
  });

  return (
    <Stack gap="sm" style={{ flex: 1, overflow: 'hidden' }}>
      <ListRangeControl presetId={filters.presetId} value={filters.range} onChange={filters.setRange} />
      {/*
        The count is every row in the date-scoped cohort that passes the chips.
        The tab loads the whole cohort rather than a page, so it no longer
        describes only what a reader has scrolled past (BAR-2738). Where the row
        cap bites, the notice above says so — that is the one case where the
        count is still a prefix, and it must not be silent.
      */}
      {shouldWarnTruncated(cohortTruncated, rows.length, cohortTotal) && (
        <CohortTruncationNotice loaded={rows.length} total={cohortTotal} />
      )}
      <ChipFilterBar state={filters.chips} count={visibleRows.length} />
      <Paper style={{ flex: 1, overflow: 'hidden' }}>
        <MantineReactTable table={table} />
      </Paper>
    </Stack>
  );
}
